import moment from "moment";
import Form from "./Form";

/**
 * Verifica se a data fornecida está em atraso ou não.
 *
 * @param {string} date Data prevista para a entrega da nota.
 * @param {number} daysDue Número de dias de antecipação para considerar a data em atraso.
 * @returns {boolean} Retorna true se a data estiver em atraso e false caso contrário.
 */
export const checkIfDateIsDue = (date, daysDue = 0) => {
    const dueDate = moment(date).subtract(daysDue, "days");

    return moment().isAfter(dueDate);
};

/**
 * Aplica uma classe CSS baseada nas definições de aviso de vencimento da unidade.
 *
 * @param {string} date Data prevista para a entrega da nota.
 * @param {number} daysDue Número de dias de antecipação para considerar a data em atraso.
 * @param {boolean} unitUseDueInvoiceAlert Indica se a unidade usa alerta de vencimento fatura.
 * @param {number} unitDueInvoiceAlertDays Dias de antecedencia para alerta de fatura vencida.
 * @returns {string} Retorna a classe CSS que deve ser aplicada a linha da tabela.
 */
export const rowClassByDueDate = (date, unitUseDueInvoiceAlert = false, unitDueInvoiceAlertDays = 0) => {
    if (!date || !unitUseDueInvoiceAlert) {
        return "";
    }

    const notaEmAtraso = checkIfDateIsDue(date, unitDueInvoiceAlertDays);

    return notaEmAtraso ? "table-danger" : "";
};

/**
 * Formata uma data para o formato dd/mm/yyyy
 *
 * @param {string} value Data para formatação.
 * @param {string} fmt Formato de data.
 * @returns {string} Retorna a data formatada.
*/
export const formatDate = (value, fmt = "DD/MM/YYYY") => {
    return value == null ? "" : moment(value, "YYYY-MM-DD").format(fmt);
};

/**
 * Formata uma data para o formato dd/mm/yyyy hh:mm:ss
 *
 * @param {string} value Data para formatação.
 * @param {string} fmt Formato de data.
 * @returns {string} Retorna a data formatada.
*/
export const formatDateTime = (value, fmt = "DD/MM/YYYY HH:mm:ss") => {
    return value == null ? "" : moment(value, "YYYY-MM-DD HH:mm:ss").format(fmt);
};

/**
 * Aplica a formatação de um CNPJ para um numero valido.
 *
 * @param {string} identification Número de identificação que precisa conter 14 digitos.
 * @returns {string} Retorna o número de identificação formatado como
 *                   CNPJ (NN.NNN.NNN/NNNN-NN), ou o valor original se não for válido.
 */
export const formatCNPJ = (identification) => {
    const sanitized = sanitizeAlphanumeric((identification || "").toString()).toUpperCase();
    if (sanitized.length == 14) {
        return sanitized.replace(/([A-Z0-9]{2})([A-Z0-9]{3})([A-Z0-9]{3})([A-Z0-9]{4})([A-Z0-9]{2})/, "$1.$2.$3/$4-$5");
    }
    return identification;
};

/**
 * Formata CNPJ a partir de partes (base, filial e dígito) ou de um valor único.
 *
 * @param {string|number} cnpjBase Base do CNPJ (ou CNPJ completo).
 * @param {string|number} cnpjFilial Filial do CNPJ.
 * @param {string|number} cnpjDigito Dígito do CNPJ.
 * @returns {string} CNPJ formatado como NN.NNN.NNN/NNNN-NN quando houver 14 caracteres alfanuméricos.
 */
export const formatCNPJFromParts = (cnpjBase, cnpjFilial, cnpjDigito) => {
    const hasParts = cnpjFilial != null || cnpjDigito != null;
    const base = sanitizeAlphanumeric((cnpjBase || "").toString()).toUpperCase();
    const filial = sanitizeAlphanumeric((cnpjFilial || "").toString()).toUpperCase();
    const digito = sanitizeAlphanumeric((cnpjDigito || "").toString()).toUpperCase();

    const merged = hasParts ? `${base}${filial}${digito}` : base;
    if (merged.length !== 14) {
        return merged;
    }
    return formatCNPJ(merged);
};

/**
 * Aplica a formatação de CPF para um numero valido.
 *
 * @param {string} identification Número de identificação que precisa conter 11 digitos.
 * @returns {string} Retorna o número de identificação formatado como CPF (###.###.###-##)
 *                   u o valor original se não for válido.
 */
export const formatCPF = (identification) => {
    if (identification != null && identification.length == 11) {
        return identification.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
    }
    return identification;
  };
export const formatToBrazilianDate = (value) => moment(value).utc(true).format("DD/MM/YYYY - HH:mm:ss");
export const documentFormat = (value) => {
    if (!value) return;
    const sanitized = sanitizeAlphanumeric(value.toString()).toUpperCase();

    const documentFormatMap = {
        11: [/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4"],
        14: [/([A-Z0-9]{2})([A-Z0-9]{3})([A-Z0-9]{3})([A-Z0-9]{4})([A-Z0-9]{2})/, "$1.$2.$3/$4-$5"]
    };

    const [documentPattern, replacementFormat] = documentFormatMap[sanitized.length] || [];
    return documentPattern ? sanitized.replace(documentPattern, replacementFormat) : value;
};
export const formatDateSubmit = (value, fmt = 'DD/MM/YYYY') => {
    return !value ? '' : moment(value, 'YYYY-MM-DD HH:mm').format(fmt);
}
export const buildQueryParams = (params) => {
    const removeEmptyParams = Object.fromEntries(
        Object.entries(params).filter(([_, value]) => value != null)
    );
    return new URLSearchParams(removeEmptyParams).toString();
}
export const sanitizeAlphanumeric = (value) => value.replace(/[^a-z0-9]/gi, "")


/**
 * Busca os dados do usuário no servidor e armazena no state do VueX.
 *
 * @param {Object} store Objeto Vuex que contem a store do projeto.
 *
 * @returns {Promise} Retorna um promise que sera resolvido quando a requisição
 *                   for finalizada.
 */
export const setUserData = async (store) => {
    const form = new Form();
    try {
        const resp = await form.get('/api/usuario');
        store.commit('setBcliente', resp.user.bcliente_active);
        store.commit('setUser', resp);

        store.getters.logged.user.unidade_active != undefined ?
            store.commit('setUnidade', resp.user.unidade_active) :
            store.commit('setUnidade', null);

        store.commit('BPlano', resp.user.bcliente_active.b_bcliente_bplano);
    }
    catch (errors) {
        new ReturnApi(errors, this)
    };
}